import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { StyleSheet } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import Animated, { useAnimatedScrollHandler } from 'react-native-reanimated';
import ListLoader from 'design-system/ListLoader';
import ListEmptyView from 'design-system/ListEmptyView';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import { selectActiveTradingAccountId, selectTradingAccountActivitySections, selectTradingAccountActivitiesInitialLoading, } from '../reducers/selectors';
import { getActivities } from '../actions';
import { getGenericActivityType } from '../utils';
import InvestActivityRow from './InvestActivityRow';
import { isWeb } from '../../../constants';
const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);
const keyExtractor = (item) => ('id' in item ? item.id : `${item.month}-${item.year}`);
const InvestActivityTab = ({ types, index, combinedScrollY, emptyPlaceholder }) => {
    const [mounted, setMounted] = React.useState(index === 0);
    const store = useAppStore();
    const dispatch = useAppDispatch();
    const flashListData = useAppSelector((state) => selectTradingAccountActivitySections(state, getGenericActivityType(types)));
    React.useEffect(() => {
        const timeout = setTimeout(() => {
            setMounted(true);
        }, 300);
        return () => {
            clearTimeout(timeout);
        };
    }, []);
    const shouldLoadSecondPage = useRef(false);
    const tradingAccountId = useAppSelector(selectActiveTradingAccountId);
    const key = `${tradingAccountId}-${getGenericActivityType(types)}`;
    const loading = useAppSelector((state) => state.unpersistedInvestReducer.requestingActivitiesForTradingAccount?.[key]);
    const initialLoading = useAppSelector((state) => selectTradingAccountActivitiesInitialLoading(state, key));
    useEffect(() => {
        if (!initialLoading) {
            if (tradingAccountId) {
                dispatch(getActivities({
                    paging: {
                        page: 1,
                        perPage: 30,
                    },
                    types,
                    accountId: tradingAccountId,
                }));
            }
        }
        // Runs only once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tradingAccountId, types]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', undefined, paddingHorizontalStyle);
    const scrollHandler = useAnimatedScrollHandler(({ contentOffset }) => {
        const itemIndex = index;
        // eslint-disable-next-line no-param-reassign
        combinedScrollY.value = {
            ...combinedScrollY.value,
            [itemIndex]: contentOffset.y,
        };
    }, []);
    const renderItem = useCallback(({ item, index }) => <InvestActivityRow item={item} isFirstIndex={index === 0}/>, []);
    const loadMoreData = useCallback(() => {
        const token = store.getState().unpersistedInvestReducer.requestingActivitiesPaginationTokenForTradingAccount?.[key];
        if (token && tradingAccountId) {
            dispatch(getActivities({
                paging: {
                    token,
                },
                types,
                accountId: tradingAccountId,
            }));
        }
    }, [key, tradingAccountId, types]);
    useEffect(() => {
        if (!loading && shouldLoadSecondPage.current) {
            shouldLoadSecondPage.current = false;
            loadMoreData();
        }
    }, [loadMoreData, loading]);
    const onLoadNextPage = useCallback(() => {
        if (loading) {
            shouldLoadSecondPage.current = true;
        }
        else {
            loadMoreData();
        }
    }, [loadMoreData, loading]);
    const ListEmptyComponent = useMemo(() => {
        if (initialLoading && !flashListData?.length) {
            return <ListLoader smallMargin/>;
        }
        return (<ListEmptyView alt onCard style={styles.empty} title={`No ${emptyPlaceholder} found`} subTitle="There’s nothing to show right now."/>);
    }, [emptyPlaceholder, flashListData?.length, initialLoading]);
    if (!mounted)
        return null;
    return (<AnimatedFlashList data={flashListData} renderItem={renderItem} scrollEventThrottle={16} onScroll={scrollHandler} onEndReachedThreshold={0.1} keyExtractor={keyExtractor} showsVerticalScrollIndicator onEndReached={onLoadNextPage} ListEmptyComponent={ListEmptyComponent} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}/>);
};
export default InvestActivityTab;
const styles = StyleSheet.create({
    empty: {
        marginTop: rem(16),
    },
});
